// shared/analytics/channel.ts
/**
 * ONE channel classifier, imported by BOTH the tracker (to build the first/latest-touch note)
 * and the collector (to classify the first pageview of a session). Own-hosts live here only.
 */
import type { Channel, ClickKind } from './enums';

export const OWN_HOSTS = ['la-mirage.in', 'lamirage.co', 'lamirage.live'] as const;

export interface Utm { s?: string; m?: string; c?: string; ct?: string; t?: string }
export interface ChannelInput {
  referrerHost?: string | null;
  utm?: Utm | null;
  clickKind?: ClickKind | null;
}
/** A first- or latest-touch note entry (stored in localStorage lm_ft / lm_lt). */
export interface Touch { ch: Channel; src?: string; med?: string; cmp?: string; lp: string; d: string }

export function normHost(h: string | null | undefined): string {
  return (h ?? '').trim().toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
}

export function isOwnHost(host: string | null | undefined): boolean {
  const h = normHost(host);
  return !!h && OWN_HOSTS.some((o) => h === o || h.endsWith(`.${o}`));
}

const PAID_MEDIUMS = new Set([
  'cpc', 'ppc', 'paid', 'paidsearch', 'paid_search', 'paid-social', 'paid_social', 'paidsocial',
  'ads', 'ad', 'display', 'cpm',
]);

const SOURCE_MAP: ReadonlyArray<[RegExp, Channel]> = [
  [/^(google|bing|duckduckgo|yahoo)$/, 'search'],
  [/^(facebook|fb|meta)$/, 'facebook'],
  [/^(instagram|ig)$/, 'instagram'],
  [/^(whatsapp|wa)$/, 'whatsapp'],
  [/^(youtube|yt)$/, 'youtube'],
  [/^(email|newsletter|mail|brevo)$/, 'email'],
];

const REFERRER_MAP: ReadonlyArray<[RegExp, Channel]> = [
  [/(^|\.)googleadservices\.com$|(^|\.)googlesyndication\.com$|^syndicatedsearch\.goog$/, 'google_ads'],
  [/^mail\.google\.com$|^outlook\.(live|office)\.com$|^mail\.yahoo\.com$/, 'email'],
  [/(^|\.)google\.[a-z.]+$|(^|\.)bing\.com$|(^|\.)duckduckgo\.com$|(^|\.)yahoo\.com$|(^|\.)ecosia\.org$|(^|\.)yandex\.[a-z.]+$|(^|\.)baidu\.com$/, 'search'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)facebook\.com$|(^|\.)fb\.com$|^fb\.me$/, 'facebook'],
  [/^wa\.me$|(^|\.)whatsapp\.com$/, 'whatsapp'],
  [/(^|\.)youtube\.com$|^youtu\.be$/, 'youtube'],
];

export function classifyChannel(i: ChannelInput): Channel {
  const ref = normHost(i.referrerHost);
  const src = (i.utm?.s ?? '').trim().toLowerCase();
  const med = (i.utm?.m ?? '').trim().toLowerCase();
  const paid = PAID_MEDIUMS.has(med);

  if (i.clickKind === 'gclid') return 'google_ads';
  if (paid && /^(google|adwords|gads)$/.test(src)) return 'google_ads';
  if (paid && /^(facebook|fb|instagram|ig|meta)$/.test(src)) return 'meta_ads';
  if (paid && i.clickKind === 'fbclid') return 'meta_ads';
  if (med === 'email') return 'email';
  if (src) {
    for (const [re, ch] of SOURCE_MAP) if (re.test(src)) return ch;
    return 'referral';
  }
  // fbclid is appended to EVERY outbound Facebook/Instagram link, organic included.
  if (i.clickKind === 'fbclid') return /(^|\.)instagram\.com$/.test(ref) ? 'instagram' : 'facebook';
  if (!ref) return 'direct';
  if (isOwnHost(ref)) return 'internal';
  for (const [re, ch] of REFERRER_MAP) if (re.test(ref)) return ch;
  return 'referral';
}

export function sourceOf(i: ChannelInput): string | null {
  const s = (i.utm?.s ?? '').trim().toLowerCase();
  if (s) return s.slice(0, 80);
  const r = normHost(i.referrerHost);
  return r && !isOwnHost(r) ? r.slice(0, 80) : null;
}
